import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getImageUrl } from "@msys/common";
import { AddAPhoto as AddAPhotoIcon } from "@mui/icons-material";
import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { GetApp as DownloadIcon } from "@mui/icons-material";
import { RotateRight as RotateRightIcon } from "@mui/icons-material";
import { CircularProgress, IconButton, ImageListItemBar, Link, Stack, useTheme, } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGesture } from "@use-gesture/react";
import { useEffect, useRef, useState } from "react";
import { animated, useSprings } from "react-spring";
import { useLatest } from "react-use";
import { is3dModel, isImage, } from "../attachments/helpers.js";
import { useIconButtonBlueStyles, useIconButtonDarkStyles, useIconButtonLightStyles, } from "../button/useIconButtonStyles.js";
import { useDownloadFile } from "../hooks/useDownloadFile.js";
import { useScreenWidth } from "../hooks/useScreenWidth.js";
import { MenuButton } from "../menu/MenuButton.js";
import { MenuItemWithIcon } from "../menu/MenuItemWithIcon.js";
import { transientOptions } from "../styles.js";
import { ThreeDViewer } from "../threeD/ThreeDViewer.js";
const clamp = (input, lower, upper) => Math.min(Math.max(input, lower), upper);
// Based on: https://github.com/farbenmeer/react-spring-slider/blob/main/src/index.tsx
export const GallerySlider = ({ images, slideIndex, onSlideIndexChange, autoScrollMs = 0, hasArrows = true, hasBullets = false, slidesAtOnce = 1, slidesToSlide = 1, handleClick, handleAdd, handleRotate, handleDelete, showDownloadButton = true, showImageName = true, disabled = false, width, height, style, downloadTitle, addTitle, deleteTitle, rotateTitle, threeDInteractive = false, arButtonTitle, }) => {
    const sliderRef = useRef(null);
    const [slide, setSlide] = useState(slideIndex ?? 0);
    const isDraggingRef = useRef(false);
    const onChangeLatest = useLatest(onSlideIndexChange);
    // Initialize slides with spring
    const [springProps, springPropsRef] = useSprings(images.length, (index) => ({
        offset: index - slide,
    }));
    const currentImage = images[slide % images.length];
    // Bindings to set on the element
    const gestureBinds = useGesture({
        onDrag: state => {
            const { down, 
            // @ts-ignore FIXME typescript fail on check
            movement: [xDelta, yDelta], 
            // @ts-ignore FIXME typescript fail on check
            direction: [xDir], 
            // @ts-ignore FIXME typescript fail on check
            distance: [xDistance, yDistance], 
            // @ts-ignore FIXME typescript fail on check
            cancel, 
            // @ts-ignore FIXME typescript fail on check
            active, } = state;
            if (!isDraggingRef.current &&
                (Math.abs(xDelta) > 10 || Math.abs(yDelta) > 10)) {
                isDraggingRef.current = true;
            }
            if (sliderRef.current?.parentElement) {
                const { width } = sliderRef.current.parentElement.getBoundingClientRect();
                if (down && Math.abs(xDistance) > width / 4) {
                    if (cancel)
                        cancel();
                    if (active) {
                        setSlide(clamp(slide + (xDir > 0 ? -1 : 1), 0, images.length - slidesAtOnce));
                    }
                }
                // see:  https://github.com/react-spring/react-spring/issues/861
                // @ts-ignore
                springPropsRef
                    .update(index => ({
                    offset: (active && down ? xDelta : 0) / width + (index - slide),
                }))
                    .start();
            }
        },
        onClick: ({ event }) => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                return;
            }
            // // Calls onClick on the current slide
            const index = slide % images.length;
            handleClick?.(index, images[index]);
        },
    }, {
        drag: {
            delay: 50,
            enabled: Boolean(currentImage && isImage(currentImage)),
        },
    });
    // Triggered on slide change
    useEffect(() => {
        // see:  https://github.com/react-spring/react-spring/issues/861
        // @ts-ignore
        springPropsRef.update(index => ({ offset: index - slide })).start();
        onChangeLatest.current?.(slide);
    }, [slide, springPropsRef, onChangeLatest]);
    // Effect for auto-sliding
    useEffect(() => {
        let interval;
        if (autoScrollMs > 0) {
            interval = setTimeout(() => {
                const targetIndex = (slide + 1) % images.length;
                setSlide(targetIndex);
            }, autoScrollMs);
        }
        return () => {
            if (interval)
                clearTimeout(interval);
        };
    }, [autoScrollMs, images.length, slide]);
    // Jump to slide index when prop changes
    useEffect(() => {
        if (slideIndex !== undefined)
            setSlide(slideIndex % images.length);
    }, [slideIndex, images.length]);
    // Sets pointer events none to every child and preserves styles
    const imagesToRender = images.map((image, index) => (_jsxs(StyledImageWrapper, { 
        // We use onClick callback from useGesture
        // onClick={handleClick ? () => handleClick(index, image) : undefined}
        role: handleClick ? "button" : "initial", "$clickable": Boolean(handleClick), children: [isImage(image) ? (_jsx(StyledPicture, { draggable: false, src: getImageUrl({ url: image.url }), alt: image.title })) : is3dModel(image) ? (_jsx(ThreeDViewer, { modelUrl: image.url, cameraControls: threeDInteractive, ar: threeDInteractive, autoRotate: threeDInteractive, arButtonTitle: arButtonTitle, fullScreen: false, paddingBottom: showImageName ? 48 : 0 })) : null, showImageName && (_jsx(ImageListItemBar, { sx: {
                    background: theme => theme.palette.mode === "dark"
                        ? `rgba(0, 0, 0, 0.5)`
                        : `rgba(80, 135, 195, 0.35)`,
                }, 
                /* eslint-disable-next-line no-irregular-whitespace */
                title: `${index + 1}/${images.length}   ${image.title}`, position: "bottom" }))] }, index)));
    const goToFirstSlide = () => {
        setSlide(0);
    };
    const goToLastSlide = () => {
        setSlide(images.length - slidesAtOnce);
    };
    const nextSlide = () => {
        const reachedLastSlide = slide === images.length - slidesAtOnce;
        const nextSlideExists = slide + (slidesAtOnce - 1) + slidesToSlide < images.length - 1;
        if (reachedLastSlide) {
            goToFirstSlide();
        }
        else if (!nextSlideExists) {
            goToLastSlide();
        }
        else {
            setSlide(slide + slidesToSlide);
        }
    };
    const previousSlide = () => {
        if (slide === 0) {
            goToLastSlide();
            return;
        }
        else if (slide - slidesToSlide <= 0) {
            goToFirstSlide();
            return;
        }
        else {
            setSlide(slide - slidesToSlide);
        }
    };
    // TODO
    // const bullets = () => {
    //   const array = [];
    //   for (let index = 0; index <= images.length - slidesAtOnce; index++) {
    //     array.push(
    //       <BulletComponent
    //         key={index}
    //         isActive={index === slide}
    //         onClick={() => setSlide(index)}
    //         style={bulletStyle}
    //       />
    //     );
    //   }
    //   return array;
    // };
    return (_jsxs(StyledWrapper, { onMouseDown: e => {
            e.stopPropagation();
        }, onTouchStart: e => {
            e.stopPropagation();
        }, "$width": width, "$height": height, ref: sliderRef, style: style, children: [hasArrows && images.length > 1 && (_jsxs(_Fragment, { children: [_jsx(NavigationButtonContainer, { "$prev": true, "$next": false, children: _jsx(NavigationButton, { prev: true, next: false, onClick: previousSlide }) }), _jsx(NavigationButtonContainer, { "$prev": false, "$next": true, children: _jsx(NavigationButton, { prev: false, next: true, onClick: nextSlide }) })] })), springProps.map(({ offset }, index) => (_createElement(animated.div, { ...gestureBinds(), key: index, style: {
                    // @ts-ignore weird typing issue
                    transform: offset.to(
                    // @ts-ignore weird typing issue
                    offsetX => `translate3d(${offsetX * 100}%, 0, 0)`),
                    position: "absolute",
                    width: `${100 / slidesAtOnce}%`,
                    height: "100%",
                    willChange: "transform",
                    touchAction: "none",
                } }, imagesToRender[index]))), _jsx(Stack, { direction: "row", spacing: 1, sx: {
                    position: "absolute",
                    right: "8px",
                    top: "8px",
                    zIndex: 2,
                }, children: _jsx(ActionButtons, { currentImage: currentImage, showDownloadButton: showDownloadButton, handleAdd: handleAdd
                        ? e => {
                            e.stopPropagation();
                            handleAdd();
                        }
                        : undefined, handleRotate: handleRotate && currentImage && isImage(currentImage)
                        ? e => {
                            e.stopPropagation();
                            handleRotate(currentImage, "right");
                        }
                        : undefined, handleDelete: handleDelete && currentImage
                        ? e => {
                            e.stopPropagation();
                            handleDelete(currentImage);
                        }
                        : undefined, disabled: disabled, downloadTitle: downloadTitle, addTitle: addTitle, deleteTitle: deleteTitle, rotateTitle: rotateTitle }) })] }));
};
const NavigationButtonContainer = styled("div", transientOptions)(({ $next, $prev }) => ({
    top: "50%",
    transform: "translateY(-50%)",
    position: "absolute",
    zIndex: 2,
    display: "flex",
    ...($next ? { right: "8px" } : undefined),
    ...($prev ? { left: "8px" } : undefined),
}));
const NavigationButton = ({ onClick, next, prev, }) => {
    const { classes } = useIconButtonBlueStyles();
    return (_jsx(IconButton, { classes: classes, color: "secondary", onClick: onClick, "aria-label": next ? "Next" : prev ? "Previous" : undefined, size: "large", children: next ? _jsx(ChevronRightIcon, {}) : prev ? _jsx(ChevronLeftIcon, {}) : undefined }));
};
const ActionButtons = ({ handleAdd, handleRotate, handleDelete, showDownloadButton, currentImage, disabled, downloadTitle = "Download", addTitle = "Add", deleteTitle = "Delete", rotateTitle = "Rotate", }) => {
    const theme = useTheme();
    const { classes: classesLight } = useIconButtonLightStyles();
    const { classes: classesDark } = useIconButtonDarkStyles();
    const { downloadFileFromUrl, downloading } = useDownloadFile();
    const classes = theme.palette.mode === "dark" ? classesDark : classesLight;
    const { isMaxPhone } = useScreenWidth();
    const numberOfButtons = [
        handleAdd,
        handleDelete,
        handleRotate,
        showDownloadButton,
    ].filter(item => !!item).length;
    if (numberOfButtons === 0 || !currentImage)
        return null;
    if (isMaxPhone && numberOfButtons > 1) {
        return (_jsxs(MenuButton, { buttonProps: {
                classes,
                size: "large",
                style: { flexShrink: 0, flexGrow: 0, width: 48, height: 48 },
            }, children: [handleRotate && (_jsx(MenuItemWithIcon, { disabled: disabled, icon: _jsx(RotateRightIcon, {}), onClick: handleRotate, children: rotateTitle })), showDownloadButton && (_jsx(MenuItemWithIcon, { component: Link, icon: !downloading ? (_jsx(DownloadIcon, {})) : (_jsx(CircularProgress, { color: "inherit", size: "1rem" })), download: currentImage.title, target: "_blank", rel: "noopener noreferrer", href: currentImage.url, onClick: async (e) => {
                        e.stopPropagation();
                        if (e.ctrlKey || e.metaKey)
                            return;
                        e.preventDefault();
                        if (currentImage)
                            await downloadFileFromUrl(currentImage.url, currentImage.title);
                    }, children: downloadTitle })), handleDelete && (_jsx(MenuItemWithIcon, { disabled: disabled, icon: _jsx(DeleteIcon, {}), onClick: handleDelete, children: deleteTitle })), handleAdd && (_jsx(MenuItemWithIcon, { disabled: disabled, icon: _jsx(AddAPhotoIcon, {}), onClick: handleAdd, children: addTitle }))] }));
    }
    return (_jsxs(_Fragment, { children: [handleRotate && (_jsx(IconButton, { classes: classes, color: "primary", disabled: disabled, onClick: handleRotate, size: "large", children: _jsx(RotateRightIcon, {}) })), showDownloadButton && (_jsx(IconButton, { classes: classes, color: "primary", component: Link, download: currentImage.title, target: "_blank", rel: "noopener noreferrer", href: currentImage.url, onClick: async (e) => {
                    e.stopPropagation();
                    if (e.ctrlKey || e.metaKey)
                        return;
                    e.preventDefault();
                    if (currentImage)
                        await downloadFileFromUrl(currentImage.url, currentImage.title);
                }, size: "large", children: !downloading ? (_jsx(DownloadIcon, {})) : (_jsx(CircularProgress, { color: "inherit", size: "1.5rem" })) })), handleDelete && (_jsx(IconButton, { classes: classes, color: "primary", disabled: disabled, onClick: handleDelete, size: "large", children: _jsx(DeleteIcon, {}) })), handleAdd && (_jsx(IconButton, { classes: classes, color: "primary", disabled: disabled, onClick: handleAdd, size: "large", children: _jsx(AddAPhotoIcon, {}) }))] }));
};
// TODO
// const StyledBulletList = styled("ul")`
//   display: flex;
//   justify-content: center;
//   list-style-type: none;
//   padding: 0;
//   margin: 15px 0;
// `;
const StyledWrapper = styled("div", transientOptions)(({ $width = "100%", $height = "100%", $maxHeight, $aspectRatio }) => ({
    position: "relative",
    overflow: "hidden",
    width: $width,
    height: $height,
    maxHeight: $maxHeight,
    aspectRatio: $aspectRatio,
}));
const StyledImageWrapper = styled("div", transientOptions)(({ $clickable }) => ({
    display: "flex",
    position: "relative",
    height: "100%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    flexShrink: 1,
    userSelect: "none",
    ...($clickable ? { cursor: "pointer" } : undefined),
}));
const StyledPicture = styled("img")({
    display: "flex",
    objectFit: "contain",
    maxWidth: "100%",
    maxHeight: "100%",
    userSelect: "none",
    touchAction: "none",
    pointerEvents: "none",
});
//# sourceMappingURL=GallerySlider.js.map